import { useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type { DunningSystemPromptVersion, DunningSystemPromptVersionsResponse } from "../types"
import type { AutoEvaluation } from "../evaluation/batch/AutoEvaluateOnSave"

const PROMPT_VERSIONS_URL = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_system_prompt_versions`

const errorMessage = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback

export type CreatePromptVersionParams = {
  content: string
  model: string
  /** true で作成と同時に有効化。省略時は下書き（非有効）として保存 */
  activate?: boolean
  /** 省略時はサーバ既定（dunning_default） */
  promptKey?: string
  /** 指定すると、保存した版でこの箱のまとめて評価を始める（有効化とは独立） */
  autoEvaluateCaseSet?: string
  /** 何を試した版かの短いメモ（任意） */
  note?: string
}

/** 版のメモの上限（文字数。API と同じ） */
export const PROMPT_NOTE_MAX_LENGTH = 100

/**
 * AIドラフトのシステムプロンプト・バージョン管理:
 * - GET  … 全バージョン一覧（promptKey ごとに新しい順）
 * - POST … 新バージョン作成（上書き更新APIは仕様として存在しない。「編集」= 新バージョン作成）
 * - PATCH :id … メモだけを書き換える（本文・モデルは変えない）
 * - POST :id/activate … 指定バージョンを有効化（ロールバックにも使う）
 * 422（バリデーション等）はサーバのメッセージをそのまま表示する。
 */
export const useDunningSystemPromptVersions = () => {
  const { getWithToken, postWithToken, patchWithToken } = useSmoothApi()

  const listSwr = useSWR(
    PROMPT_VERSIONS_URL,
    async (fetchUrl: string) => {
      const response = await getWithToken(fetchUrl)
      return response.data as DunningSystemPromptVersionsResponse
    },
    { revalidateOnFocus: false }
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [autoEvaluation, setAutoEvaluation] = useState<AutoEvaluation | null>(null)

  const createVersion = async (
    params: CreatePromptVersionParams
  ): Promise<DunningSystemPromptVersion | undefined> => {
    setIsSubmitting(true)
    setSubmitError(null)
    setAutoEvaluation(null)
    try {
      // リクエストキーは camelCase のままで良い（サーバ側で snake_case に変換される）
      const response = await postWithToken(PROMPT_VERSIONS_URL, {
        promptKey: params.promptKey,
        content: params.content,
        model: params.model,
        activate: params.activate === true,
        ...(params.note?.trim() ? { note: params.note.trim() } : {}),
        ...(params.autoEvaluateCaseSet ? { autoEvaluateCaseSet: params.autoEvaluateCaseSet } : {}),
      })
      await listSwr.mutate()
      const body = response.data as {
        data: DunningSystemPromptVersion
        autoEvaluation?: AutoEvaluation | null
      }
      setAutoEvaluation(body.autoEvaluation ?? null)
      return body.data
    } catch (e) {
      setSubmitError(errorMessage(e, "プロンプトの保存に失敗しました。"))
      return undefined
    } finally {
      setIsSubmitting(false)
    }
  }

  /** 指定バージョンを有効化（過去バージョンへのロールバックにも使う） */
  const activateVersion = async (
    versionId: number
  ): Promise<DunningSystemPromptVersion | undefined> => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const response = await postWithToken(`${PROMPT_VERSIONS_URL}/${versionId}/activate`, {})
      await listSwr.mutate()
      return (response.data as { data: DunningSystemPromptVersion }).data
    } catch (e) {
      setSubmitError(errorMessage(e, "バージョンの有効化に失敗しました。"))
      return undefined
    } finally {
      setIsSubmitting(false)
    }
  }

  /** 版のメモを書き換える（空なら消す）。失敗したら例外を投げる（呼び出し側がその場で知らせる） */
  const updateNote = async (
    versionId: number,
    note: string
  ): Promise<DunningSystemPromptVersion> => {
    try {
      const response = await patchWithToken(`${PROMPT_VERSIONS_URL}/${versionId}`, {
        note: note.trim(),
      })
      await listSwr.mutate()
      return (response.data as { data: DunningSystemPromptVersion }).data
    } catch (e) {
      throw new Error(errorMessage(e, "メモの保存に失敗しました。"))
    }
  }

  return {
    versions: listSwr.data?.data ?? [],
    isLoading: !listSwr.data && !listSwr.error,
    error: listSwr.error,
    createVersion,
    activateVersion,
    updateNote,
    isSubmitting,
    submitError,
    clearSubmitError: () => setSubmitError(null),
    /** 直近の保存で始めた（または始めなかった）まとめて評価 */
    autoEvaluation,
    clearAutoEvaluation: () => setAutoEvaluation(null),
  }
}
